export const stepOneData = [
  {
    heading: "Why Use Minemi’s EMI Calculator for Car Loans?",
    // paras before heading
    paraOne:
      "An Equated Monthly Installment (EMI) is the fixed amount you pay monthly towards repaying your loan. It comprises both the principal and interest components. Estimating your EMI in advance helps in budgeting and ensures that the loan remains affordable throughout its tenure.",

    impactHead: "Impact of Loan Tenure on EMI and Total Repayment",

    infoList: [
      {
        heading: "Accurate EMI Estimates: ",
        description:
          "Get precise monthly payment calculations based on your loan amount, interest rate, and tenure.",
      },
      {
        heading: "Compare Multiple Lenders: ",
        description:
          "Evaluate loan offers side-by-side to find the best deal for your dream car.",
      },
      {
        heading: "Prepayment Insights: ",
        description:
          "See how partial prepayments can reduce your EMI or loan tenure.",
      },
      {
        heading: "Loan Affordability: ",
        description:
          "Plan how much you can comfortably borrow based on your income and financial obligations.",
      },
      {
        heading: "Separate Rates for New & Used Cars: ",
        description:
          "Understand that interest rates for new cars are generally lower than those for used cars due to reduced risks for lenders.",
      },
      {
        heading: "Simple & Time-Saving: ",
        description:
          "Minemi makes it easy to calculate and plan your car loan in minutes.",
      },
    ],
  },
];

export const stepTwoData = [
  {
    heading: "What You Can Do With the Car Loan EMI Calculator",

    infoList: [
      {
        heading: "Estimate Monthly Payments: ",
        info: "Enter your loan amount, interest rate, and tenure to get an instant estimate of your car loan EMI.",
      },
      {
        heading: "Compare Loan Offers: ",
        info: "Adjust parameters like tenure, loan amount, and interest rate to simulate various loan scenarios and find the most affordable option.",
      },
      {
        heading: "Plan Prepayments: ",
        info: "Use the calculator to understand how prepaying a portion of your loan can reduce your EMI or loan tenure.",
      },
      {
        heading: "Personal loan refinance calculator: ",
        info: "Consider whether to refinance an existing loan.",
      },
    ],

    tipHead: "Choose the Right Tenure for Your Loan",
    tipList: [
      {
        list: "Shorter tenures mean higher EMIs but lower overall interest payments.",
      },
      {
        list: "Longer tenures reduce EMIs but increase the total cost of the loan.",
      },
      {
        list: "Use Minemi’s Car Loan EMI Calculator to find the perfect balance between monthly affordability and long-term savings.",
      },
    ],
  },
];

export const stepThreeData = [
  {
    heading: "Sample EMI Calculation for Car Loans",

    calculationTable: {
      heading: [
        {
          head: "Car Type",
        },
        {
          head: "Loan Amount",
        },
        {
          head: "Interest Rate (%)",
        },
        {
          head: "Tenure (Years)",
        },
        {
          head: "EMI (₹)",
        },
        {
          head: "Total Interest (₹)",
        },
        {
          head: "Total Payment (₹)",
        },
      ],
      row: [
        {
          carType: "New Car",
          loanAmount: "₹6,00,000",
          interest: "8.0%",
          tenure: "5",
          emi: "₹12,167",
          totalInterest: "₹1,30,020",
          totalPayment: "₹7,30,020",
        },
        {
          carType: "Used Car",
          loanAmount: "₹6,00,000",
          interest: "10.5%",
          tenure: "5",
          emi: "₹12,867",
          totalInterest: "₹1,72,020",
          totalPayment: "₹7,72,020",
        },

        {
          carType: "New Car",
          loanAmount: "₹8,00,000",
          interest: "7.5%",
          tenure: "3",
          emi: "₹24,884",
          totalInterest: "₹95,824",
          totalPayment: "₹8,95,824",
        },
        {
          carType: "Used Car",
          loanAmount: "₹6,00,000",
          interest: "10.0%",
          tenure: "3",
          emi: "₹25,812",
          totalInterest: "₹1,11,232",
          totalPayment: "₹9,11,232",
        },
      ],
    },
    note: "Interest rates for used cars are typically higher than those for new cars due to greater risks and depreciation. Use Minemi’s calculators for customized results.",

    tipHead: "Negotiate for Better Interest Rates",
    tipList: [
      {
        list: "Improve your credit score to qualify for lower interest rates.",
      },
      {
        list: "Compare offers from multiple lenders and negotiate better terms.",
      },
      {
        list: "Look for festive discounts or special promotions to reduce your interest rate.",
      },
    ],
  },
];

export const stepFourData = [
  {
    heading: "How to Maximize Savings on Your Car Loan",
    subtitle:
      "The interest rate directly impacts your EMI and the total cost of the loan. A lower interest rate reduces both your monthly payments and the overall amount payable.",

    negotiatingHead: "Negotiating Interest Rates:",
    list: [
      {
        head: "Make a Bigger Down Payment: ",
        info: "A higher down payment reduces the loan amount, leading to lower EMIs and interest costs.",
      },
      {
        head: " Compare Interest Rates: ",
        info: "Even a small reduction in the interest rate can save you thousands over the loan tenure. Use Minemi to compare rates for both new and used cars.",
      },
      {
        head: "Avoid Over-Borrowing: ",
        info: "Borrow only what you need and can comfortably repay. Use the EMI calculator to find the optimal loan amount.",
      },
      {
        head: "Opt for Prepayment: ",
        info: "Pay off a portion of your loan early to reduce your EMI or loan tenure.",
      },
    ],
    tipHead: "Watch Out for Hidden Charges",
    tipList: [
      {
        list: "Processing fees, foreclosure charges, and documentation fees can increase your loan cost.",
      },
      {
        list: "Always ask for a breakdown of charges before finalizing your loan.",
      },
      {
        list: "Use Minemi’s resources to understand and account for these costs.",
      },
    ],
  },
];
export const stepFiveData = [
  {
    heading: "How Credit Score Affects Interest Rates and EMI",
    subtitle:
      "Your credit score is a numerical representation of your creditworthiness. Lenders use it to assess the risk of lending to you.",

    creditScoreList: [
      {
        head: "High Credit Score (750 and above): ",
        info: "Indicates low risk, leading to lower interest rates and EMIs.",
      },
      {
        head: "Moderate Credit Score (650-749): ",
        info: "May result in standard interest rates.",
      },
      {
        head: "Low Credit Score (below 650): ",
        info: "Considered high risk, leading to higher interest rates and EMIs.",
      },
    ],

    improvingCredit: "Improving Your Credit Score:",
    improveCreditlist: [
      {
        head: "Timely Payments: ",
        info: "Always pay your EMIs and credit card bills on time.",
      },
      {
        head: "Credit Utilization Ratio: ",
        info: "Keep your credit card usage below 30% of the limit.",
      },
      {
        head: "Avoid Multiple Loan Applications: ",
        info: "Too many loan inquiries can negatively impact your score.",
      },
    ],
    tip: "Regularly check your credit report for errors and get them rectified to maintain an accurate credit score.",
  },
];
